<template>
  <div>
    <p class="h6">
      Log In to Your Profile
    </p>
    <template v-if="!order.business">
      <div class="social-login-buttons">
        <div class="social-btn google-btn">
          <GoogleLoginButton :form-type="formType" @login="$emit('continue')" />
        </div>
        <div class="social-btn">
          <FacebookLoginButton :form-type="formType" @login="$emit('continue')" />
        </div>
        <div class="social-btn">
          <LinkedinLoginButton :form-type="formType" @login="$emit('continue')" />
        </div>
      </div>
      <small v-if="$store.state.auth.loginAlert" class="alert-error">Please use your company email address to log in to your business account.</small>
      <span class="or-line">or</span>
    </template>
    <form class="login-form" method="post" @submit.prevent="login">
      <div class="input-group">
        <input
          id="email"
          v-model="email"
          type="email"
          name="email"
          :class="{ 'invalid-field': emailAlert }"
          autocomplete="username"
          placeholder="Email"
        >
        <small v-if="emailAlert" class="alert">{{ emailAlert }}</small>
      </div>
      <div class="input-group">
        <input
          id="password"
          v-model="password"
          :type="visiblePassword ? 'text' : 'password'"
          name="password"
          :class="{ 'invalid-field': passwordAlert }"
          autocomplete="current-password"
          placeholder="Password"
        >
        <small v-if="passwordAlert" class="alert">{{ passwordAlert }}</small>
        <Component
          :is="visiblePassword ? 'IconEye' : 'IconEyeClosed'"
          class="icon-eye"
          @click="visiblePassword = !visiblePassword"
        />
      </div>
      <button class="btn-primary submit btn-lg" :disabled="!email || !password">
        Login
      </button>
    </form>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import FacebookLoginButton from './FacebookLoginButton';
import GoogleLoginButton from './GoogleLoginButton';
import LinkedinLoginButton from './LinkedinLoginButton';
import IconEye from '../../assets/svg/eye.svg?vue-component';
import IconEyeClosed from '../../assets/svg/eye-closed-lash.svg?vue-component';

export default {
  components: {
    FacebookLoginButton,
    GoogleLoginButton,
    LinkedinLoginButton,
    IconEye,
    IconEyeClosed,
  },
  data() {
    return {
      visiblePassword: false,
      email: '',
      password: '',
      formType: 'login',
      emailAlert: '',
      passwordAlert: '',
    };
  },
  computed: {
    ...mapState('checkout', ['order', 'existingEmail']),
  },
  mounted() {
    this.email = this.existingEmail;
  },
  methods: {
    async login() {
      if (!this.email || !this.password) {
        return;
      }

      const login = await this.$store.dispatch('auth/login', { email: this.email, password: this.password });

      if (login) {
        if (login.status !== 'error') {
          this.$emit('continue');
        } else if (login.status === 'error') {
          this.emailAlert = '';
          this.passwordAlert = '';

          if (login.error === 'incorrectPassword') {
            this.passwordAlert = login.message;
          } else if (login.status === 'error') {
            this.emailAlert = login.message;
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.h6 {
  text-transform: capitalize;
  color: var(--main-light-shade-3);
}

.social-login-buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .social-btn {
    background: var(--main-dark-shade-3);
    border: 1px solid var(--dark-gray);
    box-sizing: border-box;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 52px;

    &:nth-of-type(2) {
      margin: 0 var(--spacing-24);
    }
  }
}

.or-line {
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  grid-column-gap: 22px;
  align-items: center;
  margin: var(--spacing-12) 0;

  &:before,
  &:after {
    content: "";
    display: block;
    height: 1px;
    background-color: var(--main-dark-light-3);
  }
}

.alert-error {
  position: static;
  text-align: center;
  display: block;
  margin-top: var(--spacing-8);
  height: auto;
  background-color: var(--main-red-light-2);
  border: 1px solid var(--main-red-light-3);
  border-radius: 5px;
  color: var(--main-red-shade-1);
  font-weight: 500;
  font-size: 14px;
  line-height: 130%;
  padding: 12px 30px;
}

form {
  .input-group {
    position: relative;
    padding-bottom: 18px;

    &:last-of-type {
      padding-bottom: 40px;
    }

    input {
      background: #4B5461;
      border: 1px solid #565E6A;
      box-sizing: border-box;
      border-radius: 5px;
      color: var(--main-dark-shade-4);

      &::placeholder {
        color: var(--main-dark-shade-4);
      }
    }

    input.invalid-field {
      border-color: var(--main-red);
    }

    .alert {
      color: var(--main-red);
    }

    .icon-eye {
      cursor: pointer;
      position: absolute;
      right: var(--spacing-16);
      top: 14px;
    }
  }

  button {
    width: 100%;
    max-width: 200px;
  }
}

@include bp($bp-mobile) {
  .social-login-buttons {
    .social-btn:not(.google-btn) {
      max-width: 52px;

      &:nth-of-type(2) {
        margin: 0 var(--spacing-8);
      }
    }
  }

  form {
    button {
      max-width: 100%;
    }
  }
}

.fa {
  .h6 {
    margin-bottom: var(--spacing-40);
    color: var(--main-light);
  }

  form {
    .input-group {
      padding-bottom: var(--spacing-12);

      &:last-of-type {
        padding-bottom: var(--spacing-40);
      }

      input {
        color: var(--main-light);
        background: var(--main-dark-light-2);
        border: 1px solid var(--main-dark-light-3);
      }
    }
  }

  .social-login-buttons {
    .social-btn {
      background: var(--main-dark-light-2);
      border: 1px solid var(--main-dark-light-3);
    }
  }

  @include bp($bp-mobile) {
    .h6 {
      margin-bottom: var(--spacing-24);
    }
  }
}
</style>
